import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn, UserPlus } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    nombre: "",
    apellido: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await authAPI.login({
          email: formData.email,
          password: formData.password,
        });
        login(response.data.user, response.data.access_token);
        toast.success("Bienvenido a ECCLESIA");
        navigate("/");
      } else {
        await authAPI.register(formData);
        toast.success("Cuenta creada exitosamente. Por favor inicia sesión.");
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Ha ocurrido un error. Intenta de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background image with blur */}
      <div
        className="absolute inset-0 bg-cover bg-center blur-sm"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1668027140957-ad854578b4d5?w=1920&q=80')",
        }}
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-rose-950/80" />

      {/* Login card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="glass rounded-xl shadow-2xl p-8 animate-fade-in">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-serif text-rose-950 tracking-tight mb-2">
              ECCLESIA
            </h1>
            <p className="text-stone-600 text-sm tracking-widest uppercase">
              Plataforma de Servicios Parroquiales
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre" className="text-stone-700">
                    Nombre
                  </Label>
                  <Input
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Juan"
                    required={!isLogin}
                    className="h-11 bg-white border-stone-200 focus:border-rose-900 focus:ring-rose-900/20"
                    data-testid="register-nombre-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apellido" className="text-stone-700">
                    Apellido
                  </Label>
                  <Input
                    id="apellido"
                    name="apellido"
                    value={formData.apellido}
                    onChange={handleChange}
                    placeholder="Pérez"
                    required={!isLogin}
                    className="h-11 bg-white border-stone-200 focus:border-rose-900 focus:ring-rose-900/20"
                    data-testid="register-apellido-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-stone-700">
                Correo Electrónico
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="correo@parroquia.org"
                required
                className="h-11 bg-white border-stone-200 focus:border-rose-900 focus:ring-rose-900/20"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-stone-700">
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  className="h-11 bg-white border-stone-200 focus:border-rose-900 focus:ring-rose-900/20 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-rose-950 text-white hover:bg-rose-900 font-medium"
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="animate-pulse">Procesando...</span>
              ) : isLogin ? (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Iniciar Sesión
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Crear Cuenta
                </>
              )}
            </Button>
          </form>

          {/* Toggle */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-stone-600 hover:text-rose-900 transition-colors"
              data-testid="toggle-auth-btn"
            >
              {isLogin
                ? "¿No tienes cuenta? Regístrate"
                : "¿Ya tienes cuenta? Inicia sesión"}
            </button>
          </div>
        </div>

        {/* Diocese info */}
        <p className="text-center text-white/70 text-sm mt-6">
          ECCLESIA · Sistema de Gestión Parroquial
        </p>
      </div>
    </div>
  );
}
